import React, { useEffect, useState } from "react";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import {
  crmDeleteSubtask,
  crmGetStaff,
  crmGetTask,
  crmGetTasks,
  crmSaveSubtask,
  crmSaveTask,
} from "../../../../api/crmApi";
import {
  AttachmentList,
  CommentThread,
  CrmDrawer,
  Field,
  GhostBtn,
  PrimaryBtn,
  SelectInput,
  StaffSearchSelect,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TextInput,
  priorityClass,
} from "./crmShared";

const CrmTasks = () => {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subTitle, setSubTitle] = useState("");

  const load = async () => {
    setRows(await crmGetTasks({}));
  };

  useEffect(() => {
    load().catch(() => setRows([]));
    crmGetStaff().then(setStaff).catch(() => setStaff([]));
  }, []);

  const openTask = async (id) => {
    const t = await crmGetTask(id);
    const assignees = t.assignees || t.Assignees || [];
    t.assigneeUserIds = assignees.map((a) => a.userId || a.UserId);
    setEditing(t);
  };

  const save = async (e) => {
    e.preventDefault();
    const assigneeUserIds = editing.assigneeUserIds || editing.AssigneeUserIds || [];
    const names = staff
      .filter((u) => assigneeUserIds.includes(u.id || u.Id))
      .map((u) => u.userName || u.UserName);
    const id = await crmSaveTask({
      ...editing,
      assigneeUserIds,
      assigneeUserNames: names,
    });
    await openTask(editing.id || editing.Id || id);
    await load();
  };

  return (
    <Box top>
      <SetupTittle text="CRM Tasks" />
      <p className="text-sm text-stone-500 mb-3">
        Kanban board. Drag a card to change status. New leads appear here automatically.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <PrimaryBtn onClick={() => setEditing({ title: "", status: "Open", priority: "Medium", assigneeUserIds: [] })}>
          New task
        </PrimaryBtn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TASK_STATUSES.map((col) => (
          <div
            key={col}
            className="rounded-2xl border border-stone-200 bg-stone-50 p-3 min-h-[280px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={async (e) => {
              e.preventDefault();
              const id = Number(e.dataTransfer.getData("taskId"));
              if (!id) return;
              const row = (rows || []).find((r) => (r.id || r.Id) === id);
              if (!row) return;
              await crmSaveTask({ ...row, status: col, id });
              await load();
            }}
          >
            <p className="font-semibold mb-2">{col}</p>
            {(rows || [])
              .filter((r) => (r.status || r.Status) === col)
              .map((r) => (
                <button
                  type="button"
                  key={r.id || r.Id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("taskId", String(r.id || r.Id))}
                  onClick={() => openTask(r.id || r.Id)}
                  className="w-full text-left rounded-xl border border-stone-200 bg-white p-3 mb-2 hover:border-orange-400 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{r.title || r.Title}</p>
                    <span className={`text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 ${priorityClass(r.priority || r.Priority)}`}>
                      {r.priority || r.Priority || "Medium"}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">{r.assigneeNames || r.AssigneeNames || "Unassigned"}</p>
                  {r.relatedType || r.RelatedType ? (
                    <p className="text-[11px] text-stone-400 mt-1">
                      {(r.relatedType || r.RelatedType) === "query" ? "lead" : (r.relatedType || r.RelatedType)} #{r.relatedId || r.RelatedId}
                    </p>
                  ) : null}
                </button>
              ))}
          </div>
        ))}
      </div>

      {editing ? (
        <CrmDrawer title={editing.id || editing.Id ? "Task" : "New task"} onClose={() => setEditing(null)}>
          <form className="space-y-3" onSubmit={save}>
            <Field label="Title">
              <TextInput value={editing.title ?? editing.Title} onChange={(v) => setEditing({ ...editing, title: v })} />
            </Field>
            <Field label="Status">
              <SelectInput value={editing.status ?? editing.Status} onChange={(v) => setEditing({ ...editing, status: v })} options={TASK_STATUSES} allowEmpty={false} />
            </Field>
            <Field label="Priority">
              <SelectInput value={editing.priority ?? editing.Priority ?? "Medium"} onChange={(v) => setEditing({ ...editing, priority: v })} options={TASK_PRIORITIES} allowEmpty={false} />
            </Field>
            <Field label="Description">
              <textarea
                className="w-full border border-stone-300 rounded-xl p-2"
                rows={3}
                value={editing.description ?? editing.Description ?? ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </Field>
            <Field label="Related to">
              <SelectInput
                value={editing.relatedType ?? editing.RelatedType}
                onChange={(v) => setEditing({ ...editing, relatedType: v })}
                options={["query", "order"]}
              />
            </Field>
            <p className="text-xs text-stone-400 -mt-2">Use query for a lead, order for a CRM order.</p>
            <Field label="Related id">
              <TextInput
                type="number"
                value={editing.relatedId ?? editing.RelatedId ?? ""}
                onChange={(v) => setEditing({ ...editing, relatedId: v === "" ? null : Number(v) })}
              />
            </Field>
            <div>
              <p className="font-semibold text-sm text-stone-700">Assign to</p>
              <div className="mt-1">
                <StaffSearchSelect
                  staff={staff}
                  selectedIds={editing.assigneeUserIds || editing.AssigneeUserIds || []}
                  onChange={(ids) => setEditing({ ...editing, assigneeUserIds: ids })}
                  placeholder="Search by name or email"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <PrimaryBtn type="submit">Save</PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
            </div>
          </form>

          {editing.id || editing.Id ? (
            <>
              <div className="mt-5">
                <p className="font-semibold mb-2">Subtasks</p>
                {(editing.subtasks || editing.Subtasks || []).map((s) => (
                  <label key={s.id || s.Id} className="flex items-center gap-2 text-sm mb-1">
                    <input
                      type="checkbox"
                      checked={!!(s.isDone ?? s.IsDone)}
                      onChange={async (e) => {
                        await crmSaveSubtask({
                          id: s.id || s.Id,
                          taskId: editing.id || editing.Id,
                          title: s.title || s.Title,
                          isDone: e.target.checked,
                        });
                        await openTask(editing.id || editing.Id);
                      }}
                    />
                    <span className={s.isDone || s.IsDone ? "line-through text-stone-400" : ""}>
                      {s.title || s.Title}
                    </span>
                    <button
                      type="button"
                      className="text-red-600 text-xs"
                      onClick={async () => {
                        await crmDeleteSubtask(s.id || s.Id);
                        await openTask(editing.id || editing.Id);
                      }}
                    >
                      Remove
                    </button>
                  </label>
                ))}
                <div className="flex gap-2 mt-2">
                  <TextInput value={subTitle} onChange={setSubTitle} />
                  <PrimaryBtn
                    onClick={async () => {
                      if (!subTitle.trim()) return;
                      await crmSaveSubtask({
                        taskId: editing.id || editing.Id,
                        title: subTitle,
                        isDone: false,
                      });
                      setSubTitle("");
                      await openTask(editing.id || editing.Id);
                    }}
                  >
                    Add
                  </PrimaryBtn>
                </div>
              </div>
              <CommentThread relatedType="task" relatedId={editing.id || editing.Id} />
              <AttachmentList relatedType="task" relatedId={editing.id || editing.Id} />
            </>
          ) : null}
        </CrmDrawer>
      ) : null}
    </Box>
  );
};

export default CrmTasks;
